// Agents ranked by week-over-week pass-rate delta.
import React, { useMemo } from 'react'
import { isEmpty, isNil } from 'lodash'

import { apiService } from '@/api'

import { CHART } from '@/components/charts/chartPalette'
import { derivePriorWindow, escapeHtml } from '../helpers/format'
import { useFiltersForPickedScope } from '../helpers/dashboardParams'
import { ChartWidgetCard, axisTooltip, useEntityClickEvents, useWidgetData } from './chartWidget'

export const TopMoversWidget = ({ scope }) => {
  const filters = useFiltersForPickedScope(scope)
  const priorWindow = useMemo(
    () => derivePriorWindow(filters?.startDate, filters?.endDate),
    [filters?.startDate, filters?.endDate]
  )

  // No prior window (open-ended range) -> null URL -> empty state.
  // Fetch errors fall through to the same empty state on purpose.
  const { data: comparison, loading } = useWidgetData(scope, (params) => {
    if (!priorWindow) {
      return null
    }
    params.set('prior_start_date', priorWindow.priorStart)
    params.set('prior_end_date', priorWindow.priorEnd)
    return `${apiService.scorecard}/scoring/scores/clickhouse/agents_period_comparison?${params.toString()}`
  })

  // Top 5 climbers + top 5 fallers by signed pp delta. Agents with a
  // null prior-period delta (no prior calls) are dropped, and so are
  // agents with zero calls THIS period -- their pass rate is
  // undefined, not 0%, and a "-40pp decline" for someone who simply
  // took no calls reads as a performance drop.
  const rows = useMemo(() => {
    const all = (comparison?.agents || [])
      .filter((agent) => !isNil(agent.pass_rate_delta_pp))
      .filter((agent) => (agent.this_period?.call_count ?? 0) > 0)
      .map((agent) => ({
        name:
          `${agent.first_name || ''} ${agent.last_name || ''}`.trim() || `User ${agent.user_id}`,
        user_id: agent.user_id,
        delta: parseFloat(agent.pass_rate_delta_pp),
        thisRate: agent.this_period?.pass_rate,
        priorRate: agent.prior_period?.pass_rate,
        thisCalls: agent.this_period?.call_count,
        priorCalls: agent.prior_period?.call_count,
      }))
    if (isEmpty(all)) {
      return []
    }
    const sorted = [...all].sort((rowA, rowB) => rowB.delta - rowA.delta)
    const top = sorted.slice(0, 5)
    // Cap bottom so a small cohort doesn't overlap top with bottom.
    const bottom = sorted.slice(Math.max(5, sorted.length - 5))
    return [...top, ...bottom]
  }, [comparison])

  const option = useMemo(() => {
    if (isEmpty(rows)) {
      return {}
    }
    return {
      tooltip: axisTooltip((params) => {
        const row = rows[params[0].dataIndex]
        if (!row) {
          return ''
        }
        const sign = row.delta >= 0 ? '+' : ''
        const thisCalls = isNil(row.thisCalls) ? '' : ` over ${row.thisCalls} calls`
        const priorCalls = isNil(row.priorCalls) ? '' : ` over ${row.priorCalls}`
        return `${escapeHtml(row.name)}<br/>Δ ${sign}${row.delta.toFixed(1)} points<br/>Now: ${row.thisRate?.toFixed(1)}%${thisCalls} (was ${row.priorRate?.toFixed(1)}%${priorCalls})<br/><em>Click for details</em>`
      }),
      grid: { left: 140, right: 40, top: 16, bottom: 32 },
      xAxis: {
        type: 'value',
        name: 'Δ points vs prior',
        axisLine: { onZero: true },
        axisLabel: {
          fontSize: 10,
          formatter: (value) => (value >= 0 ? `+${value}` : `${value}`),
        },
      },
      yAxis: {
        type: 'category',
        data: rows.map((row) => row.name),
        inverse: true,
        axisLabel: { fontSize: 11 },
      },
      series: [
        {
          type: 'bar',
          cursor: 'pointer',
          barWidth: '60%',
          data: rows.map((row) => ({
            value: parseFloat(row.delta.toFixed(1)),
            // Drilldown payload embedded on the point -- the click
            // handler must read params.data (events bind once at mount).
            agent: { user_id: row.user_id, full_name: row.name, passing_average: row.thisRate },
            itemStyle: {
              color: row.delta >= 0 ? CHART.SUCCESS : CHART.CRITICAL,
              borderRadius: row.delta >= 0 ? [0, 3, 3, 0] : [3, 0, 0, 3],
            },
          })),
          label: {
            show: true,
            position: 'right',
            fontSize: 10,
            formatter: ({ value }) => `${value >= 0 ? '+' : ''}${value}pp`,
          },
        },
      ],
    }
  }, [rows])

  const events = useEntityClickEvents('agent', 'agent')

  return (
    <ChartWidgetCard
      title="Top Movers — biggest weekly pass-rate change"
      info="Agents whose pass rate moved the most versus the prior period — the biggest improvements and the biggest drops to follow up on."
      hint="Click an agent to open their drawer"
      empty={isEmpty(rows)}
      emptyText="Not enough prior-period data to compute movers."
      loading={loading}
      option={option}
      events={events}
      height="320px"
    />
  )
}
